import { onboardingLogger } from './loggerInstance';
import api from '../services/apiService';

/**
 * Simple function to check if the current user has completed onboarding
 * This is a lightweight API call that should be fast and reliable
 * No local persistence: cache is managed in React Query.
 *
 * @returns Promise<boolean | null> - true if user has completed onboarding,
 *                                    false if they need to complete it,
 *                                    null if the check failed or was inconclusive
 */
export async function checkUserOnboardingStatus(): Promise<boolean | null> {
  try {
    const response = await api.get('/api/v1/users/has-onboarding');

    // Only return a definitive result if we get a successful response
    if (response.status === 200) {
      const data = response.data;

      // Completion is driven by backend `has_onboarding` (derived from completed_at).
      if (data?.has_onboarding === true) {
        return true;
      }

      // If API explicitly says not completed, return false
      if (data?.has_onboarding === false) {
        onboardingLogger.debug('User needs onboarding', {
          hasOnboarding: data?.has_onboarding,
        });
        return false;
      }

      // If has_onboarding is not explicitly set, default to false for safety
      onboardingLogger.warn('has_onboarding not explicitly set, defaulting to false', {
        data,
      });
      return false;
    }

    // Non-200: do not assume completed — caller should retry or use a safe default
    onboardingLogger.warn('Onboarding check failed or inconclusive', { status: response.status });
    return null;
  } catch (error) {
    onboardingLogger.error('Error checking onboarding status', { error });
    // Re-throw the error so calling components can handle it appropriately
    // This allows components to redirect to onboarding on error
    throw error;
  }
}

/**
 * Clear onboarding cache (useful when user completes onboarding)
 */
export function clearOnboardingCache(): void {
  onboardingLogger.debug('Onboarding cache clear requested');
}

/**
 * Mark onboarding as completed in cache (called when user completes onboarding)
 */
export function markOnboardingCompleted(): void {
  onboardingLogger.debug('Onboarding completion mark requested');
}
